import React, { useEffect, useState } from 'react';
import {
  History, X, Loader2, RotateCcw, Eye, AlertCircle, Trash2, CheckCircle2,
} from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { BlocksRenderer } from './BlockRenderer';

/**
 * VersionHistoryModal — admin tool to browse and restore previous page snapshots.
 *
 * Usage:
 *   <VersionHistoryModal pageKey="home" onClose={...} onRestored={...} />
 *
 * Props:
 *   - pageKey:    string — page_key whose versions to show
 *   - onClose:    fn     — close the modal
 *   - onRestored: fn     — called AFTER a restore succeeds. Parent should
 *                          refresh the editor blocks from the server.
 */
export default function VersionHistoryModal({ pageKey, onClose, onRestored }) {
  const [versions, setVersions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState(null);

  async function loadList() {
    setLoadingList(true);
    setError('');
    try {
      const { data } = await api.get(`/admin/pages/${pageKey}/versions`);
      const list = Array.isArray(data) ? data : [];
      setVersions(list);
      if (list.length > 0 && !selectedId) {
        setSelectedId(list[0].id);  // auto-select newest
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoadingList(false);
    }
  }
  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [pageKey]);

  // Load preview blocks when a version is selected
  useEffect(() => {
    if (!selectedId) { setSelectedBlocks([]); return; }
    let alive = true;
    setLoadingPreview(true);
    api.get(`/admin/pages/${pageKey}/versions/${selectedId}`)
      .then(({ data }) => {
        if (alive) setSelectedBlocks(Array.isArray(data?.blocks) ? data.blocks : []);
      })
      .catch(() => { if (alive) setSelectedBlocks([]); })
      .finally(() => { if (alive) setLoadingPreview(false); });
    return () => { alive = false; };
  }, [selectedId, pageKey]);

  useEffect(() => {
    if (feedback) {
      const t = setTimeout(() => setFeedback(null), 3500);
      return () => clearTimeout(t);
    }
  }, [feedback]);

  async function restore(version) {
    if (!window.confirm(`¿Restaurar la versión ${version.version_label}?\n\nSe creará automáticamente un respaldo de la versión actual antes de restaurar, así que podrás deshacer este cambio desde el historial.`)) return;
    setRestoring(true);
    try {
      await api.post(`/admin/pages/${pageKey}/versions/${version.id}/restore`);
      setFeedback({ type: 'success', message: `Versión ${version.version_label} restaurada exitosamente.` });
      await loadList(); // refresh the version list (a new snapshot was created)
      onRestored?.();   // tell parent to reload its blocks
    } catch (err) {
      setFeedback({ type: 'error', message: 'Error al restaurar: ' + formatApiError(err) });
    } finally {
      setRestoring(false);
    }
  }

  async function deleteVersion(version) {
    if (!window.confirm(`¿Eliminar la versión ${version.version_label}? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/pages/${pageKey}/versions/${version.id}`);
      setFeedback({ type: 'success', message: 'Versión eliminada.' });
      if (selectedId === version.id) setSelectedId(null);
      await loadList();
    } catch (err) {
      setFeedback({ type: 'error', message: 'Error al eliminar: ' + formatApiError(err) });
    }
  }

  return (
    <div className="fixed inset-0 bg-zinc-950/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="version-history-modal">
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-14 border-b border-zinc-200 flex items-center justify-between px-5 shrink-0">
          <div className="flex items-center gap-2">
            <History size={18} className="text-cmic-600" />
            <h2 className="text-base font-semibold text-zinc-900">Historial de versiones</h2>
            <span className="text-xs text-zinc-400 ml-2">{versions.length === 1 ? '1 versión' : `${versions.length} versiones`}</span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md text-zinc-500 hover:bg-zinc-100" data-testid="version-modal-close-btn">
            <X size={18} />
          </button>
        </div>

        {feedback && (
          <div className={`px-5 py-2 text-sm border-b flex items-center gap-2 ${feedback.type === 'success' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-red-50 text-red-800 border-red-200'}`}>
            {feedback.type === 'success' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
            {feedback.message}
          </div>
        )}

        <div className="flex-1 flex overflow-hidden">
          {/* LEFT — versions list */}
          <aside className="w-72 border-r border-zinc-200 flex flex-col overflow-hidden shrink-0">
            {loadingList ? (
              <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin text-cmic-500" size={20} /></div>
            ) : versions.length === 0 ? (
              <div className="p-6 text-sm text-zinc-500 text-center">
                <p className="font-medium mb-1">Sin versiones aún</p>
                <p className="text-xs">Los snapshots se crean automáticamente cada vez que guardas la página.</p>
              </div>
            ) : (
              <div className="overflow-y-auto p-2 space-y-1" data-testid="version-list">
                {versions.map((v) => {
                  const sel = v.id === selectedId;
                  return (
                    <button
                      key={v.id}
                      onClick={() => setSelectedId(v.id)}
                      data-testid={`version-item-${v.version_label}`}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${sel ? 'bg-cmic-50 ring-1 ring-cmic-200' : 'hover:bg-zinc-50'}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-semibold ${sel ? 'text-cmic-700' : 'text-zinc-900'}`}>{v.version_label}</span>
                        <span className="text-[11px] text-zinc-400">{v.block_count} bloque{v.block_count === 1 ? '' : 's'}</span>
                      </div>
                      <div className="text-[11px] text-zinc-500 leading-relaxed">
                        {formatRelative(v.created_at)}
                      </div>
                      {v.created_by_name && (
                        <div className="text-[11px] text-zinc-500 mt-0.5 truncate">por <span className="text-zinc-700">{v.created_by_name}</span></div>
                      )}
                      {v.change_summary && (
                        <div className="text-[11px] text-zinc-500 mt-1.5 italic truncate" title={v.change_summary}>{v.change_summary}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </aside>

          {/* RIGHT — preview pane */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {selectedId ? (
              <>
                {(() => {
                  const v = versions.find((x) => x.id === selectedId);
                  if (!v) return null;
                  return (
                    <div className="px-5 h-12 border-b border-zinc-200 flex items-center justify-between shrink-0 bg-zinc-50">
                      <div className="flex items-center gap-3 min-w-0">
                        <Eye size={14} className="text-zinc-500 flex-shrink-0" />
                        <span className="text-sm font-medium text-zinc-900">Vista previa: <span className="text-cmic-600">{v.version_label}</span></span>
                        <span className="text-xs text-zinc-400 truncate">{formatRelative(v.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => deleteVersion(v)}
                          data-testid="version-delete-btn"
                          className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50 transition-colors"
                          title="Eliminar esta versión"
                        >
                          <Trash2 size={12} />
                        </button>
                        <button
                          onClick={() => restore(v)}
                          disabled={restoring}
                          data-testid="version-restore-btn"
                          className="inline-flex items-center gap-1.5 bg-cmic-600 hover:bg-cmic-700 disabled:opacity-60 text-white px-3 py-1.5 rounded-md text-xs font-semibold shadow-sm transition-colors"
                        >
                          {restoring ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                          Restaurar esta versión
                        </button>
                      </div>
                    </div>
                  );
                })()}
                <div className="flex-1 overflow-auto bg-zinc-100 p-4">
                  {loadingPreview ? (
                    <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-cmic-500" size={24} /></div>
                  ) : selectedBlocks.length === 0 ? (
                    <p className="text-center text-zinc-400 text-sm py-20">Esta versión no contiene bloques.</p>
                  ) : (
                    <div className="bg-white shadow-sm ring-1 ring-zinc-200 mx-auto max-w-4xl pointer-events-none select-none" data-testid="version-preview-canvas">
                      <BlocksRenderer blocks={selectedBlocks} locale="es" />
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-sm text-zinc-400">
                {versions.length > 0 ? 'Selecciona una versión para previsualizar.' : 'No hay versiones para mostrar.'}
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="px-5 py-3 bg-red-50 text-red-800 text-sm border-t border-red-200">
            <AlertCircle size={14} className="inline mr-1" /> {error}
          </div>
        )}
      </div>
    </div>
  );
}

function formatRelative(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return 'Hace unos segundos';
    const min = Math.floor(sec / 60);
    if (min < 60) return `Hace ${min} min`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `Hace ${hr} h`;
    const days = Math.floor(hr / 24);
    if (days < 7) return `Hace ${days} día${days === 1 ? '' : 's'}`;
    return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (_) {
    return iso;
  }
}
